import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type { TTestAccuracyResult } from '@conversed/shared/src/central/types/test-accuracy';
import { useModal } from '@hooks/modal/useModal';
import type React from 'react';
import { useTranslation } from 'react-i18next';

interface TestResultsReviewDataModalProps {
  testResult: TTestAccuracyResult;
}

export const TestResultsReviewDataModal: React.FC<
  TestResultsReviewDataModalProps
> = ({ testResult }) => {
  const { t } = useTranslation();
  const { hideModal } = useModal();

  return (
    <div className='tailwind-modal-form flex-1 px-2'>
      <div className='tailwind-modal-body'>
        <div className='mb-6 p-4 bg-gray-100 rounded-lg'>
          <div className='grid grid-cols-3 gap-4 text-center'>
            <div>
              <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                {t('test-accuracy.table.header.start-date')}
              </span>
              <span className='text-xs sm:text-sm font-medium text-gray-800'>
                {testResult.startDate}
              </span>
            </div>
            <div>
              <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                {t('test-accuracy.table.header.end-date')}
              </span>
              <span className='text-xs sm:text-sm font-medium text-gray-800'>
                {testResult.endDate}
              </span>
            </div>
            <div>
              <span className='text-tiny sm:text-xs text-gray-500 block mb-1'>
                {t('test-accuracy.table.header.accuracy')}
              </span>
              <span className='text-xs sm:text-sm font-medium'>
                {testResult.accuracy}%
              </span>
            </div>
          </div>
        </div>

        <div className='w-full h-[calc(100%-110px)] overflow-y-auto border-2 border-gray-200 rounded-tl-md rounded-tr-md'>
          <table className='w-full table-fixed'>
            <thead className='align-middle text-left font-medium bg-white sticky top-0 z-10'>
              <tr className='bg-gray-100 text-center text-tiny xs:text-xs sm:text-sm'>
                <th className='bg-inherit rounded-tl-md px-2 py-2 w-[25%]'>
                  <span className='text-primary-base font-medium'>
                    {t('test-results.modal.table.header.question')}
                  </span>
                </th>
                <th className='bg-inherit px-2 py-2 w-[25%]'>
                  <span className='text-primary-base font-medium'>
                    {t('test-results.modal.table.header.original-answer')}
                  </span>
                </th>
                <th className='bg-inherit px-2 py-2 w-[25%]'>
                  <span className='text-primary-base font-medium'>
                    {t('test-results.modal.table.header.improved-answer')}
                  </span>
                </th>
                <th className='bg-inherit px-2 py-2 rounded-tr-md w-[25%]'>
                  <span className='text-primary-base font-medium'>
                    {t('test-results.modal.table.header.new-answer')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className='font-normal'>
              {testResult.reviewData?.map((review, index) => (
                <tr
                  key={review.questionId || index}
                  className={`border-b border-gray-200 text-gray-800 text-center whitespace-nowrap text-tiny xs:text-xs sm:text-sm ${
                    review.isCorrect ? 'bg-green-light/10' : 'bg-red-light/40'
                  }`}>
                  <td className='px-2 py-2 align-top text-left bg-inherit border-gray-200'>
                    <div className='whitespace-normal break-words'>
                      <span className='text-xs'>{review.question}</span>
                    </div>
                  </td>
                  <td className='px-2 py-2 align-top text-left bg-inherit border-gray-200'>
                    <div className='whitespace-normal break-words'>
                      <span className='text-xs'>{review.originalAnswer}</span>
                    </div>
                  </td>
                  <td className='px-2 py-2 align-top text-left bg-inherit border-gray-200'>
                    <div className='whitespace-normal break-words'>
                      <span className='text-xs'>{review.improvedAnswer}</span>
                    </div>
                  </td>
                  <td className='px-2 py-2 align-top text-left bg-inherit border-gray-200'>
                    <div className='whitespace-normal break-words'>
                      <span className='text-xs'>{review.newAnswer}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className='tailwind-modal-footer'>
        <ButtonT
          onClick={hideModal}
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'>
          {t('general.buttons.close')}
        </ButtonT>
      </div>
    </div>
  );
};
